import type { Invoice } from "@medplum/fhirtypes";

import { getInvoiceFacilityAdmission } from "@/billing/components/StatementDetail/utils/facility-admission";
import { SERVICE_PAYER_CODE } from "@/billing/constants/service-payer-code";
import { isCancelledSubStatement } from "@/billing/utils/balance-returned";
import { statementNumberOf } from "@/billing/utils/invoice-statement-number";
import { isSubStatementNumber } from "@/billing/utils/statement-predecessor";
import {
  getStatementStatus,
  hasStatementTag,
  isClosedStatus,
  tagsWithout,
} from "@/billing/utils/statement-status";
import { BillingStatementTag, QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { hasFacilityFee } from "@/utils/billing";

import {
  STATEMENT_ACCEPTED_SUB_STATUS_OPTIONS,
  STATEMENT_STATUS,
  STATEMENT_STATUS_COLOR,
  StatementStatus,
} from "../constants/statements";

export { getInvoiceDueFromLabel } from "@/billing/utils/invoice-due-from-label";

/**
 * Whether the statement is blocked pending its facility admission details.
 *
 * A facility fee cannot be billed without the admission block a payer needs to adjudicate it, so a
 * statement carrying one is held read-only until those fields are filled: the biller cannot move it
 * out of Unsent.
 *
 * Not a status of its own. A blocked statement reads as `Unsent`, which is what it is — the block
 * takes away the status menu rather than replacing what the badge says, so the biller sees an unsent
 * statement they cannot send yet rather than a state the billing lifecycle does not have.
 *
 * Reuses {@link getInvoiceFacilityAdmission}, the canonical reader, rather than a second presence
 * check: it already returns `undefined` for a block missing any of its four children, so the
 * "all four or none" invariant and the child `url`s behind it stay in exactly one place.
 *
 * Derived on every read rather than stored. Filling the admission unblocks the statement and
 * clearing it blocks it again, with no write path to keep in step — which is the whole reason
 * {@link hasFacilityFee} — stamped at generation in `@/utils/billing` — is never removed. A stored
 * flag would need clearing on save and would drift out of step with reality whenever some other
 * write path (a status change, an archive, a closure date) saved the Invoice without knowing to
 * maintain it.
 */
export function isMissingFacilityAdmission(invoice: Invoice): boolean {
  return hasFacilityFee(invoice) && !getInvoiceFacilityAdmission(invoice);
}

export function statusDotColor(option: StatementStatus): string {
  return `var(--mantine-color-${STATEMENT_STATUS_COLOR[option].replace(".", "-")})`;
}

export function isAcceptedBranchStatus(status: StatementStatus): boolean {
  return (
    status === STATEMENT_STATUS.Accepted || STATEMENT_ACCEPTED_SUB_STATUS_OPTIONS.includes(status)
  );
}

/**
 * Returns a shallow copy of the invoice with the archive meta.tag added or removed, leaving
 * `Invoice.status` and every other tag — the lifecycle and delete tags included — untouched.
 * Archiving is presentational: it hides the statement from the default list without voiding it, and
 * unarchiving restores it with its billing state intact.
 */
export function applyStatementArchived(invoice: Invoice, archived: boolean): Invoice {
  const preservedTags = tagsWithout(invoice, [BillingStatementTag.Archived]);

  return {
    ...invoice,
    meta: {
      ...invoice.meta,
      tag: archived
        ? [
            ...preservedTags,
            {
              system: QuiltedHealthCodeSystems.BillingStatementTags,
              code: BillingStatementTag.Archived,
              display: "Archived",
            },
          ]
        : preservedTags,
    },
  };
}

/**
 * Returns a shallow copy of the invoice carrying the restart meta.tag, leaving `Invoice.status`,
 * the lifecycle tag and every other tag untouched.
 */
export function applyStatementRestarted(invoice: Invoice): Invoice {
  const preservedTags = tagsWithout(invoice, [BillingStatementTag.Restarted]);

  return {
    ...invoice,
    meta: {
      ...invoice.meta,
      tag: [
        ...preservedTags,
        {
          system: QuiltedHealthCodeSystems.BillingStatementTags,
          code: BillingStatementTag.Restarted,
          display: "Restarted",
        },
      ],
    },
  };
}

/**
 * Whether the statement may be deleted. Only unsent statements qualify: once a statement has been
 * sent it is part of the billing record the payer or client has already seen, and withdrawing it
 * belongs to the void path rather than this one.
 *
 * A statement blocked pending facility admission details is an unsent one, so it stays deletable:
 * the block stops it being sent, not discarded.
 *
 * A sub-statement is never deleted. Its balance belongs to its parent, so an unsent one is removed
 * by cancelling it, which returns that balance.
 */
export function canDeleteStatement(invoice: Invoice): boolean {
  if (isSubStatementNumber(statementNumberOf(invoice))) return false;
  return getStatementStatus(invoice) === STATEMENT_STATUS.Unsent;
}

/**
 * Whether the statement may be restarted. Only the statuses a payer closed out qualify, and only
 * once: restarting hands the charges back to the Services tab, so a second restart would have
 * nothing left to return and would unbill charges a later statement had since claimed.
 */
export function canRestartStatement(invoice: Invoice): boolean {
  if (hasStatementTag(invoice, BillingStatementTag.Restarted)) return false;

  const status = getStatementStatus(invoice);
  return status === STATEMENT_STATUS.Rejected || status === STATEMENT_STATUS.Denied;
}

/**
 * Whether the statement's status has settled and may no longer be changed.
 *
 * `Paid` and `Denied` are the payer's final word on a statement: the money is settled, or the claim
 * is closed out. Every other status keeps its menu, right up to the point a decision is recorded.
 * The terminal pair is named once, in {@link isClosedStatus}, which this routes through.
 *
 * Reads the stored status rather than the badge's rollup: {@link getPrimaryStatementStatus} shows an
 * insurance `Paid` as `Accepted` with the finer status beneath, so testing the rollup would leave
 * every accepted-branch statement editable.
 *
 * A statement deleted *after* settling reads as `Cancelled` here, because {@link getStatementStatus}
 * answers with the delete tag before it looks at anything else. That costs nothing in practice:
 * {@link canDeleteStatement} admits only `Unsent` statements, so a settled one never reaches that
 * state through the product, and a deleted statement's badge is read-only either way. Recovering
 * the settled status through a delete would need a marker stored for the purpose, which this rule
 * does not require.
 *
 * Restart is a separate question — {@link canRestartStatement} still admits a `Denied` statement,
 * because handing its charges back to Services is the recovery path for a denied claim and is
 * guarded to run once. This guard covers editing the status, not leaving the closed state.
 *
 * A cancelled sub-statement is locked as well: its balance has been moved to the parent, so
 * moving it back to a live status would bill it twice.
 *
 * @param invoice The statement being read.
 * @returns True when the statement is Paid, Denied, or a cancelled sub-statement.
 */
export function isStatementStatusLocked(invoice: Invoice): boolean {
  const status = getStatementStatus(invoice);
  if (isClosedStatus(status)) return true;
  return isCancelledSubStatement(invoice);
}

/**
 * Returns a shallow copy of the invoice carrying the soft-delete meta.tag, leaving every tag
 * outside the archive and delete codes untouched — the lifecycle tag included.
 *
 * Deleting clears the archive tag: the two are alternative views of the same list, and a deleted
 * statement has left the one archiving moves it between. Leaving both tags on would put the
 * statement in a state no view offers a way out of, since archiving is disabled once deleted.
 *
 * Deleting also moves `Invoice.status` to `cancelled`, which the badge reads back as Deleted: the
 * tag drives which list the statement appears in, and the status records that it is no longer a
 * live claim. There is no inverse — deleting is one-way, so the function only ever moves a
 * statement into the deleted state.
 *
 * Deletion is a soft delete for the same reason archiving is presentational: the statement drops
 * out of every default list without the resource being voided or destroyed, so it stays auditable
 * and its billing state stays intact.
 */
export function applyStatementDeleted(invoice: Invoice): Invoice {
  const preservedTags = tagsWithout(invoice, [
    BillingStatementTag.Deleted,
    BillingStatementTag.Archived,
  ]);

  return {
    ...invoice,
    status: "cancelled",
    meta: {
      ...invoice.meta,
      tag: [
        ...preservedTags,
        {
          system: QuiltedHealthCodeSystems.BillingStatementTags,
          code: BillingStatementTag.Deleted,
          display: "Deleted",
        },
      ],
    },
  };
}

/**
 * The status the badge actually shows for a statement with the given payer.
 *
 * `Accepted` is a rollup: an insurance statement on the accepted branch reads as `In Processing`,
 * `Paid` or `Denied` internally, but the badge shows `Accepted` with the finer status underneath.
 * The branch is insurance-only — a self-pay statement reaches `Paid` or `Denied` without ever being
 * accepted — so the payer decides whether the rollup applies. Anything else shows its own status.
 *
 * Callers that order or group rows by the visible badge should read this rather than the raw status,
 * so their result matches what the biller sees.
 */
export function getPrimaryStatementStatus(
  status: StatementStatus,
  payer: (typeof SERVICE_PAYER_CODE)[keyof typeof SERVICE_PAYER_CODE] | undefined,
): StatementStatus {
  const isAcceptedSubStatus =
    payer === SERVICE_PAYER_CODE.Insurance &&
    STATEMENT_ACCEPTED_SUB_STATUS_OPTIONS.includes(status);

  return isAcceptedSubStatus ? STATEMENT_STATUS.Accepted : status;
}

export {
  applyStatementStatus,
  getInvoiceClosureDate,
  getStatementStatus,
  hasStatementTag,
  isClosedStatus,
} from "@/billing/utils/statement-status";
